
import { EMPLOYEE_EMPLOYMENT_STATUS_MARKING } from "@/constants/compliance";
import {
  TEmployee,
  TEmployee_Table,
  convertEmployeesForTable,
  sortEmployeesData,
} from "@/models/employees";
import axios from "@/plugins/http";
import { SMUser } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from "vue-demi";

export default defineComponent({
  name: "VEmployeesBrowse",

  components: {
    BannersSection: () => import("./components/BannersSection.vue"),
  },

  setup() {
    const instance = getCurrentInstance()?.proxy;
    const route = instance?.$route;
    const router = instance?.$router;

    const companyId = computed(() => Number(route.params.company_id ?? 0));

    const endpoint = computed(() => {
      if (companyId.value) return API_ROUTES.EXPERT_COMPANIES_EMPLOYEES(companyId.value);

      return API_ROUTES.COMPANY_EMPLOYEES;
    });

    function navigateEmployee(item: TEmployee_Table) {
      if (!item.userId) return;

      const { userId } = item;

      const path = SMUser.isExpert
        ? `${SMUser.basePath}/companies/${companyId.value}/employees/${userId}`
        : `${SMUser.basePath}/employees/${userId}`;

      const routeData = router.resolve(path);

      window.open(routeData.href, "_blank");
    }

    const headers = [
      {
        label: "Name",
        value: "name",
        sort: true,
        sortBy: "text",
        // action: navigateEmployee,
      },
      {
        label: "Job position",
        value: "job_position",
      },
      {
        label: "Employee Type",
        value: "employeeType",
      },
      {
        label: "Immigration category",
        value: "immigrationCategory",
      },
      {
        label: "Visa Expiry Date",
        value: "visaExpiration",
        sort: true,
        sortBy: "date",
      },
      {
        label: "Status",
        value: "status",
      },
    ];

    const searchBy = ["name", "email", "job_position"];

    const dataForTabs = ref<{ active: TEmployee[]; leavers: TEmployee[] }>({
      active: [],
      leavers: [],
    });

    const activeTab = ref("active");

    const tabs = ref([
      { title: "Active", value: "active", count: dataForTabs.value.active?.length || 0 },
      { title: "Leavers", value: "leavers", count: dataForTabs.value.leavers?.length || 0 },
    ]);

    const loading = ref(false);

    async function fetchEmployees() {
      try {
        loading.value = true;

        const params = {
          list_name: activeTab.value,
        };

        const { data } = await axios.get<TEmployee>(endpoint.value, {
          params,
        });

        if (!data) return;

        dataForTabs.value[activeTab.value] = data.employee_list.map((a) =>
          convertEmployeesForTable(a)
        );

        dataForTabs.value[activeTab.value] = sortEmployeesData(dataForTabs.value[activeTab.value]);

        tabs.value.forEach((tab) => {
          if (tab.value === "active") tab.count = data.count.active_count;
          if (tab.value === "leavers") tab.count = data.count.terminated_count;
        });
      } finally {
        loading.value = false;
      }
    }

    onMounted(() => fetchEmployees());

    function onTabSelect(tab: string) {
      if (!tab || tab === activeTab.value) return;

      activeTab.value = tab;

      fetchEmployees();
    }

    const inviteEmployeeLoading = ref(false);

    async function inviteEmployee() {
      try {
        inviteEmployeeLoading.value = true;

        const { data } = await axios.get(API_ROUTES.COMPANY_EMPLOYEE_ONBOARD_EMPLOYEE);

        if (!data) return;

        const { task_id } = data;

        router.push(SMUser.basePath + `/tasks/${task_id}`);
      } finally {
        inviteEmployeeLoading.value = false;
      }
    }

    function getClass(workingStatus: string) {
      return EMPLOYEE_EMPLOYMENT_STATUS_MARKING[workingStatus] || "";
    }

    return {
      headers,
      tabs,
      dataForTabs,
      searchBy,
      loading,
      onTabSelect,
      inviteEmployee,
      inviteEmployeeLoading,
      navigateEmployee,
      getClass,
    };
  },
});
